import { createSelector } from 'reselect';
import { Platform } from 'react-native';
import { FORCE_SHOW_TRIAL } from 'config/debug';
import { selectPotProductsWithInterest, selectPotProductWithTheHighestInterest, } from 'features/pots/reducers/selectors';
import { getRequiredTier } from 'features/settings/logins/utils';
import { flatten } from 'lodash';
import { selectFeatureFlags, selectUser, selectActiveConnectionsWithoutManual, selectIsGBUser, selectFeatureFlag, selectFeatureFlagVariant, selectIsLoggedIn, selectDefaultCurrency, } from 'reducers/selectors';
import checkFeatureFlag from 'utils/featureFlags';
import { getIapSubscriptionPrices, isUnlocked } from 'utils';
import { formatTryFor0 } from 'utils/formatting';
import { benefits, benefitsData, benefitsDataHideDigital, benefitsTopFeatures, benefitsTopFeaturesGB, dynamicBenefit, filterBenefitsArray, getCustomNameAndDescription, mapBenefitsToBenefitsDataArray, } from '../benefits';
import { isAndroid, isIOS } from '../../../constants';
import { checkIsBenefitIdAvailableOnFree } from '../checkIsBenefitIdAvailableOnFree';
import { Tier, TierPeriod, itemSkus, tierLevelMap, emmaProStatusToSKU, isPremiumTier, } from '../entities';
import { canUseFreeTrial } from '../trial/utils';
import { selectIsUserOnPremium, selectUserTier } from './tier';
import { selectFamilyPlanPrices } from './stripe';
import { getAnnualDiscountPercentage } from '../annualDiscount';
export const selectCustomPotProductBenefits = createSelector([selectPotProductsWithInterest], (potProductsWithInterest) => potProductsWithInterest?.map((p) => `customPotBenefitId-${p.name}`));
export const selectBenefitsData = createSelector([
    (store) => store.user.user.guessedHomeCountry,
    selectCustomPotProductBenefits,
], (guessedHomeCountry, customPotProductBenefits) => {
    if (guessedHomeCountry !== 'GB') {
        return benefitsData;
    }
    const benefitsDataWithInterestPotProducts = benefitsDataHideDigital.map((category) => {
        if (category.name === 'Investments & Savings') {
            return {
                ...category,
                data: [
                    ...(customPotProductBenefits ?? []),
                    ...(category.data ?? []),
                ],
            };
        }
        return category;
    });
    return benefitsDataWithInterestPotProducts;
});
export const selectFilteredBenefits = createSelector([
    (store) => store.user.user.guessedHomeCountry,
    (store) => store.utils.featureFlags,
    selectPotProductsWithInterest,
    selectCustomPotProductBenefits,
], (guessedHomeCountry, featureFlags, potProducts, customBenefitsData) => {
    const hasInterestPotProduct = !!potProducts?.length;
    const customBenefits = {};
    customBenefitsData?.forEach((benefit) => {
        customBenefits[benefit] = dynamicBenefit(benefit);
    });
    const result = filterBenefitsArray(Object.values({ ...benefits, ...customBenefits }), {
        homeCountry: guessedHomeCountry,
    }, featureFlags, hasInterestPotProduct);
    return result;
});
export const selectFilteredBenefitsData = createSelector([selectFilteredBenefits, selectBenefitsData], (benefitsArray, benefitsData) => {
    const benefits = benefitsArray.reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {});
    return mapBenefitsToBenefitsDataArray(benefits, benefitsData);
});
export const selectActiveTier = createSelector([(store) => store.user.user], (user) => (user.emmaProStatus.active ? user.emmaProStatus.tier : undefined));
export const selectIsOnReverseTrial = (store) => store.user.user.emmaProStatus.reverseTrial?.active &&
    !store.user.user.emmaProStatus.paying;
export const selectIsOnFreeTrial = (store) => !!store.user.user.emmaProStatus.isOnFreeTrial;
export const selectPaymentFailed = (store) => store.user.user.emmaProStatus.paymentFailed;
export const selectHasFailedPayment = (store) => !!store.user.user.emmaProStatus.paymentFailed ||
    !!store.sharedPlans.extraMembersInfo?.paymentFailed;
export const selectSeatBasedPaymentFailed = (store) => store.sharedPlans.extraMembersInfo?.paymentFailed;
export const selectAllIAPSubscriptionsWithPrices = createSelector([(store) => store.iapSubscriptions.subscriptions], (iapSubscriptions) => Object.values(iapSubscriptions || {})
    .map(getIapSubscriptionPrices)
    .filter((subscription) => subscription !== undefined));
export const selectAvailableIAPSubscriptionsWithPrices = createSelector([selectAllIAPSubscriptionsWithPrices], (iapSubscriptions) => iapSubscriptions.filter((subscription) => itemSkus.find((sku) => sku.id === subscription.id)));
const LIMIT_DEFAULT = {
    [Tier.free]: 2,
    [Tier.plus]: 4,
    [Tier.pro]: null,
    [Tier.ultimate]: null,
};
// Unused but keep in case we flag for tests
export const selectConnectionsLimit = createSelector([selectFeatureFlags], () => LIMIT_DEFAULT);
const DEFAULT_AGENCIES = {
    [Tier.free]: null,
    [Tier.plus]: ['EXPERIAN'],
    [Tier.pro]: ['EXPERIAN', 'EQUIFAX', 'TRANSUNION'],
    [Tier.ultimate]: ['EXPERIAN', 'EQUIFAX', 'TRANSUNION'],
};
export const selectRentReportingAgenciesTierMap = createSelector([(store) => store.utils.featureFlags], (featureFlags) => featureFlags.rent_reporting_two?.extra?.availableAgencies &&
    Object.keys(featureFlags.rent_reporting_two?.extra.availableAgencies)
        .length > 0
    ? featureFlags.rent_reporting_two?.extra.availableAgencies
    : DEFAULT_AGENCIES);
export const selectRentReportingAgenciesForOnboarding = createSelector([
    (store) => store.user.user.emmaProStatus,
    selectRentReportingAgenciesTierMap,
], (emmaProStatus, rentReportingAgenciesTierMap) => {
    if (emmaProStatus.active &&
        emmaProStatus.tier &&
        rentReportingAgenciesTierMap[emmaProStatus.tier]) {
        return rentReportingAgenciesTierMap[emmaProStatus.tier];
    }
    return rentReportingAgenciesTierMap[Tier.pro];
});
export const selectIsPlatformPaySupported = (store) => store.stripe.isPlatformPaySupported;
const selectIsStripeAvailableForCountryAndPlatform = createSelector([
    (store) => store.user.user.guessedHomeCountry,
    selectIsPlatformPaySupported,
    (store) => store.utils.featureFlags,
], (guessedHomeCountry, isPlatformPaySupported, featureFlags) => {
    const isGBUser = guessedHomeCountry === 'GB';
    const isUSUser = guessedHomeCountry === 'US';
    const isEnabledForAndroidUS = isUSUser &&
        isAndroid &&
        checkFeatureFlag(featureFlags, 'stripe_plaid_ach');
    const isEnabledForAndroidGB = isGBUser && isAndroid;
    const allowOnAndroid = (isEnabledForAndroidUS || isEnabledForAndroidGB) &&
        checkFeatureFlag(featureFlags, 'subscription_via_stripe_android');
    const isEnabledForIOSUS = isUSUser && isIOS && checkFeatureFlag(featureFlags, 'stripe_plaid_ach');
    const isEnabledForIOSGB = isGBUser && isIOS;
    const allowOnIOS = (isEnabledForIOSUS || isEnabledForIOSGB) &&
        checkFeatureFlag(featureFlags, 'subscription_via_stripe_ios');
    const isCardFallbackEnabled = checkFeatureFlag(featureFlags, 'subscription_via_stripe_card_fallback');
    return ((isPlatformPaySupported || isCardFallbackEnabled) &&
        (allowOnIOS || allowOnAndroid));
});
export const selectShouldShowStripe = createSelector([
    (store) => store.user.user.emmaProStatus,
    selectIsStripeAvailableForCountryAndPlatform,
], (emmaProStatus, isStripeAvailableForCountryAndPlatform) => {
    if (!emmaProStatus?.active)
        return isStripeAvailableForCountryAndPlatform;
    if (emmaProStatus.store === 'google' || emmaProStatus.store === 'apple') {
        return false;
    }
    if (emmaProStatus.store === 'stripe')
        return true;
    return isStripeAvailableForCountryAndPlatform;
});
export const selectStripePriceMap = (store) => store.stripe.priceMap;
export const FREE_TRIAL_TIER = Tier.pro;
export const FREE_TRIAL_DEFAULT_DAYS = 7;
export const selectDefaultFreeTrialDays = (store) => store.user.user.emmaProStatus.defaultFreeTrialDays || FREE_TRIAL_DEFAULT_DAYS;
export const selectEligibleFreeTrialDays = createSelector([
    selectShouldShowStripe,
    (store) => store.user.user.emmaProStatus.eligibleFreeTrialDays,
    selectDefaultFreeTrialDays,
], (showStripe, eligibleFreeTrialDays, defaultFreeTrialDays) => {
    if (showStripe) {
        return eligibleFreeTrialDays || defaultFreeTrialDays;
    }
    return defaultFreeTrialDays;
});
export const selectSkipFreeTrialAB = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'skip_free_trial_ab'));
export const selectAbApplePaySheet = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_apple_pay_sheet'));
export const selectWarnOnSkipAreYouSureNumbersV2 = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'warn_on_skip_are_you_sure_numbers_v2'));
export const selectShouldSeePremiumTrialScreen = createSelector([
    (store) => store.user.user.emmaProStatus.active,
    selectAvailableIAPSubscriptionsWithPrices,
    (_, tier) => tier,
    (store) => store.iapSubscriptions.isEligibleForIntroOffer,
    selectShouldShowStripe,
    (store) => store.user.user.emmaProStatus.usedFreeTrial,
], (isPremiumActive, availableSubscriptions, tier, isEligibleForIntroOffer, shouldUseStripe, usedFreeTrial) => {
    if (__DEV__ && FORCE_SHOW_TRIAL) {
        return true;
    }
    /**
     * Check if the user hasnt already used the free trial from stores too while using stripe
     */
    if (shouldUseStripe) {
        return !isPremiumActive && !usedFreeTrial;
    }
    const hasFreeTrialForTier = availableSubscriptions
        .filter((subscription) => tier === subscription.tier)
        .every((subscription) => canUseFreeTrial(subscription, isEligibleForIntroOffer));
    return !isPremiumActive && hasFreeTrialForTier;
});
export const selectFilteredBenefitsWithCustom = createSelector([
    (store) => store.invest.tierFxRateMap,
    (store) => store.invest.tierSecclAUMFeesMap,
    selectPotProductWithTheHighestInterest,
    (store) => store.paymentLimits.limits,
    selectConnectionsLimit,
    selectRentReportingAgenciesTierMap,
    selectFeatureFlags,
    selectFilteredBenefits,
    (store) => store.user.user.guessedHomeCountry,
    (store) => store.pots.savingsPlanTierFees,
    (_, tier) => tier,
    selectPotProductsWithInterest,
    selectFamilyPlanPrices,
], (tierFxRateMap, aumFeesTierMap, potProductWithTheHighestInterest, paymentLimitMap, connectionsLimitMap, rentReportingAgenciesTierMap, featureFlags, filteredBenefits, guessedHomeCountry, savingsPlanTierFees, tier, potProducts, familyPlanPrices) => {
    const currentTierLevel = tierLevelMap[tier];
    const interestPaymentFrequency = potProductWithTheHighestInterest?.interestPaymentFrequency;
    const rate = potProductWithTheHighestInterest?.rates.tierRates.find((rate) => rate.tier === tier);
    return filteredBenefits
        .map((item) => {
        const customData = getCustomNameAndDescription(item, featureFlags, tier, paymentLimitMap, rate ? { ...rate, interestPaymentFrequency } : undefined, potProducts, tierFxRateMap, aumFeesTierMap, familyPlanPrices, connectionsLimitMap, rentReportingAgenciesTierMap, guessedHomeCountry, savingsPlanTierFees, potProductWithTheHighestInterest);
        return {
            ...item,
            customData,
        };
    })
        .filter((item) => {
        const isBenefitIdAvailableOnFree = checkIsBenefitIdAvailableOnFree(item.id, guessedHomeCountry, featureFlags);
        return (((tierLevelMap[item.minTier] <= currentTierLevel &&
            (!item.maxTier ||
                tierLevelMap[item.maxTier] >= currentTierLevel ||
                tierLevelMap[item.maxTier] < tierLevelMap[item.minTier]) &&
            !isBenefitIdAvailableOnFree) ||
            (isBenefitIdAvailableOnFree && tier === Tier.free)) &&
            item.customData !== null);
    })
        .reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {});
});
export const selectFilteredBenefitsDataForTierWithCustom = createSelector([selectFilteredBenefitsWithCustom, selectBenefitsData], (benefitsWithCustom, benefitsData) => mapBenefitsToBenefitsDataArray(benefitsWithCustom, benefitsData));
export const selectFilteredBenefitsDataForTierWithCustomAndTopFeatures = createSelector([
    selectFilteredBenefitsWithCustom,
    (store) => store.user.user.guessedHomeCountry,
    selectBenefitsData,
    (store) => store.utils.featureFlags,
    (_, tier) => tier,
], (benefitsWithCustom, guessedHomeCountry, benefitsData, _featureFlags, tier) => {
    const mappedBenefitsData = tier !== 'FREE'
        ? mapBenefitsToBenefitsDataArray(benefitsWithCustom, benefitsData)
        : mapBenefitsToBenefitsDataArray(Object.fromEntries(Object.entries(benefitsWithCustom).filter(([key]) => !key.startsWith('customPotBenefitId'))), benefitsData);
    const topFeatures = {
        name: 'Top features',
        data: [],
        collapsible: false,
    };
    // Among benefits whose id starts with `creditScore`, keep only the
    // highest-tier one and drop the rest.
    let highestCreditScoreTier = -1;
    mappedBenefitsData.forEach((category) => {
        category.data.forEach((benefit) => {
            if (benefit.id.startsWith('creditScore')) {
                const level = tierLevelMap[benefit.minTier];
                if (level > highestCreditScoreTier) {
                    highestCreditScoreTier = level;
                }
            }
        });
    });
    if (highestCreditScoreTier >= 0) {
        mappedBenefitsData.forEach((category) => {
            for (let i = category.data.length - 1; i >= 0; i--) {
                const benefit = category.data[i];
                if (benefit.id.startsWith('creditScore') &&
                    tierLevelMap[benefit.minTier] < highestCreditScoreTier) {
                    category.data.splice(i, 1);
                }
            }
        });
    }
    const topFeaturesForCountry = guessedHomeCountry === 'GB'
        ? benefitsTopFeaturesGB
        : benefitsTopFeatures;
    let hasPotsInterestIndex = false;
    let hasInterestPotProducts = false;
    mappedBenefitsData.forEach((category) => {
        category.data.forEach((categoryBenefit, index) => {
            if (category.name === 'Investments & Savings') {
                if (categoryBenefit.id.startsWith('customPotBenefitId-'))
                    hasInterestPotProducts = true;
                if (categoryBenefit.id === 'potsInterest')
                    hasPotsInterestIndex = index;
            }
            if (topFeaturesForCountry[tier].includes(categoryBenefit.id)) {
                topFeatures.data.push(categoryBenefit);
            }
        });
    });
    if (hasInterestPotProducts &&
        typeof hasPotsInterestIndex === 'number' &&
        tier !== 'FREE') {
        const index = hasPotsInterestIndex;
        mappedBenefitsData.forEach((category) => {
            if (category.name === 'Investments & Savings') {
                category.data.splice(index, 1);
            }
        });
    }
    topFeatures.data.sort((a, b) => topFeaturesForCountry[tier].indexOf(a.id) -
        topFeaturesForCountry[tier].indexOf(b.id));
    if (benefitsTopFeaturesGB.FREE.includes('linkAccounts') &&
        tier === Tier.free) {
        topFeatures.data.push(benefits.linkAccounts);
    }
    return [topFeatures, ...mappedBenefitsData];
});
export const selectFilteredBenefitsDataForTierFlattened = createSelector([selectFilteredBenefitsDataForTierWithCustom], (filteredBenefits) => flatten(filteredBenefits.map((item) => item.data)));
export const selectShouldShowLoginsUpgrade = createSelector([selectActiveConnectionsWithoutManual, selectUser, selectConnectionsLimit], (connections, user, connectionsLimitMap) => {
    const requiredTier = getRequiredTier(connectionsLimitMap, connections.length);
    return requiredTier !== Tier.free && !isUnlocked(user, requiredTier);
});
export const selectIsOnSameStore = createSelector([(store) => store.user.user, selectShouldShowStripe], (user, shouldShowStripe) => {
    if (shouldShowStripe) {
        const isInEmployerPlan = 'type' in user.emmaProStatus &&
            user.emmaProStatus.active &&
            user.emmaProStatus.type &&
            user.emmaProStatus.type === 'employee';
        return isInEmployerPlan || user.emmaProStatus.store === 'stripe';
    }
    return (user.emmaProStatus.store ===
        Platform.select({ ios: 'apple', android: 'google' }));
});
export const selectStripePaymentMethodMissing = (store) => {
    const { emmaProStatus } = store.user.user;
    return Boolean(emmaProStatus.autoRenewing &&
        emmaProStatus.store === 'stripe' &&
        store.stripe.customer &&
        !store.stripe.customer?.cardLast4 &&
        !store.stripe.customer?.achPaymentMethod);
};
export const selectStripePaymentLast4 = (store) => store.stripe.customer?.cardLast4 ?? null;
export const selectShowSubscriptionPaymentFailed = createSelector([
    selectShouldShowStripe,
    selectPaymentFailed,
    selectStripePaymentMethodMissing,
], (canUseStripe, paymentFailed, missingPaymentMethod) => canUseStripe && (!!paymentFailed || missingPaymentMethod));
export const selectShowSeatBasedSubscriptionPaymentFailed = createSelector([
    selectShouldShowStripe,
    selectSeatBasedPaymentFailed,
    selectStripePaymentMethodMissing,
    (store) => store.sharedPlans.extraMembersInfo?.totalSeats ?? 0,
], (canUseStripe, seatBasedPaymentFailed, missingPaymentMethod, extraMemberSlotCount) => canUseStripe &&
    !!extraMemberSlotCount &&
    (!!seatBasedPaymentFailed || missingPaymentMethod));
export const selectSubscriptionPrices = createSelector([
    selectShouldShowStripe,
    selectStripePriceMap,
    selectAvailableIAPSubscriptionsWithPrices,
], (canUseStripe, stripePriceMap, subscriptions) => {
    if (canUseStripe) {
        return stripePriceMap;
    }
    const prices = {
        [TierPeriod.yearly]: {},
        [TierPeriod.monthly]: {},
    };
    subscriptions.forEach((sub) => {
        if (sub.period === TierPeriod.yearly) {
            prices[TierPeriod.yearly][sub.tier] = sub;
        }
        else if (sub.period === TierPeriod.monthly) {
            prices[TierPeriod.monthly][sub.tier] = sub;
        }
    });
    return prices;
}, {
    devModeChecks: { identityFunctionCheck: 'never' },
});
export const selectEmmaProStatusSKU = createSelector([(store) => store.user.user.emmaProStatus], (emmaProStatus) => emmaProStatusToSKU(emmaProStatus), {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => {
            if (a === undefined) {
                if (b === undefined) {
                    return true;
                }
                return false;
            }
            if (b === undefined) {
                return false;
            }
            return a.id === b.id && a.period === b.period && a.tier === b.tier;
        },
    },
});
export const selectDidPayFullPrice = createSelector([
    (store) => store.user.user.emmaProStatus,
    selectStripePriceMap,
    selectIsUserOnPremium,
    selectUserTier,
], (emmaProStatus, tierPrices, isPremium, userTier) => {
    const { period: userPeriod, currentPrice } = emmaProStatus;
    if (isPremium && tierPrices && currentPrice) {
        const period = userPeriod === 'yearly' ? 'YEARLY' : 'MONTHLY';
        const price = tierPrices[period][userTier];
        return (currentPrice.amount === Number(price.price) &&
            currentPrice.currency === price.currency);
    }
    return false;
});
export const selectChooseYourPlanVersionB = createSelector([(store) => store.utils.featureFlags, selectIsGBUser], (featureFlags, isGBUser) => checkFeatureFlag(featureFlags, 'choose_your_plan_version_b') && isGBUser);
export const selectShouldShowMicroModal = createSelector([
    (store) => selectFeatureFlag(store, 'micro_modals').value,
    (store) => selectFeatureFlagVariant(store, 'micro_modals').value,
    selectUser,
    (store) => store,
], (featureFlag, variant, user, store) => {
    if (featureFlag)
        return 'v1';
    if (variant === 'v2' || variant === 'v3') {
        if (selectShouldSeePremiumTrialScreen(store, user.emmaProStatus.tier ?? Tier.free))
            return variant;
    }
    return null;
});
// A/B test
export const selectIsPaywallCloseButtonShownLate = createSelector([(store) => store.utils.featureFlags, selectIsGBUser], (featureFlags, isGBUser) => checkFeatureFlag(featureFlags, 'paywall_close_button_shown_late') &&
    isGBUser);
export const selectShouldRemoveXToSkipUs = createSelector([
    (store) => store.utils.featureFlags,
    selectIsGBUser,
    selectIsLoggedIn,
], (featureFlags, isGBUser, isLoggedIn) => !isGBUser &&
    !isLoggedIn &&
    checkFeatureFlag(featureFlags, 'remove_x_to_skip_us_ab'));
export const selectBlinkistBack = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_blinkist_back') ||
    checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'v4') ||
    checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'v4_preselected_monthly') ||
    checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'clear_price_v4_monthly') ||
    checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'clear_price_v4_weekly') ||
    checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'try_for_0'));
export const selectBlinkistBackV4 = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'v4') ||
    checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'v4_preselected_monthly') ||
    checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'clear_price_v4_monthly') ||
    checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'clear_price_v4_weekly'));
// v4 layout with the monthly period pre-selected instead of yearly.
export const selectBlinkistBackV4Monthly = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'v4_preselected_monthly'));
// Original flo paywall (isFloPaywallC1) with the monthly period pre-selected
// instead of yearly. Intentionally NOT part of selectBlinkistBack so the flo
// layout still renders (it is gated on !blinkistBack).
export const selectBlinkistBackFloMonthly = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'flo_preselected_monthly'));
export const selectBlinkistBackFloYearlyTiers = createSelector([(store) => store.utils.featureFlags], (featureFlags) => {
    if (checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'flo_yearly_pro_plus'))
        return [Tier.pro, Tier.plus];
    if (checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'flo_yearly_pro_ultimate'))
        return [Tier.pro, Tier.ultimate];
    return null;
});
export const selectClearPricingMode = createSelector([(store) => store.utils.featureFlags], (featureFlags) => {
    if (checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'clear_price_v4_weekly') ||
        checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'clear_price_flo_weekly')) {
        return 'weekly';
    }
    if (checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'clear_price_v4_monthly') ||
        checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'clear_price_flo_monthly')) {
        return 'monthly';
    }
    return null;
});
export const selectBlinkistBackTryFor0 = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_blinkist_back', 'try_for_0'));
export const selectFloPaywallC1Inside = createSelector([
    (store) => store.utils.featureFlags,
    selectBlinkistBack,
    (_, isMicroModal) => isMicroModal,
], (featureFlags, blinkistBack, isMicroModal) => checkFeatureFlag(featureFlags, 'ab_flo_paywall_c1_inside') &&
    !isMicroModal &&
    !blinkistBack);
export const selectShowPaywallInsteadOfAd = createSelector([
    (store) => store.utils.featureFlags,
    (store) => store.user.user.emmaProStatus,
    selectIsGBUser,
], (featureFlags, emmaProStatus, isGBUser) => !emmaProStatus.active &&
    isGBUser &&
    checkFeatureFlag(featureFlags, 'paywall_vs_modal'));
export const selectHideViewAllPlansOnboarding = createSelector([(store) => store.utils.featureFlags, selectIsGBUser], (featureFlags, isGBUser) => isGBUser &&
    checkFeatureFlag(featureFlags, 'ab_hide_view_all_plans_onboarding'));
export const selectIsStillDecidingModal = createSelector([
    (store) => store.utils.featureFlags,
    (store) => store.user.user.emmaProStatus.active,
    selectIsGBUser,
    selectIsLoggedIn,
], (featureFlags, isPremiumActive, isGBUser, isLoggedIn) => {
    const isEnabled = checkFeatureFlag(featureFlags, 'still_deciding_modal') &&
        !isPremiumActive &&
        isGBUser &&
        !isLoggedIn;
    if (!isEnabled)
        return null;
    return featureFlags?.still_deciding_modal ?? null;
});
export const selectHidePlusFromViewAllPlansAB = createSelector([
    (store) => store.utils.featureFlags,
    selectIsGBUser,
    selectIsLoggedIn,
], (featureFlags, isGBUser, isLoggedIn) => isGBUser &&
    checkFeatureFlag(featureFlags, 'hide_plus_view_all_plans') &&
    !isLoggedIn);
export const selectChooseBankDec2025 = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'choose_bank_dec_2025'));
export const DEFAULT_UPGRADE_TEXT = 'Upgrade now';
export const selectTryFor0Text = createSelector([
    (store) => selectShouldSeePremiumTrialScreen(store, FREE_TRIAL_TIER),
    selectEligibleFreeTrialDays,
    selectDefaultCurrency,
], (shouldSeePremiumTrialScreen, eligibleFreeTrialDays, userCurrency) => {
    if (shouldSeePremiumTrialScreen && eligibleFreeTrialDays) {
        return formatTryFor0(userCurrency);
    }
    return DEFAULT_UPGRADE_TEXT;
});
export const selectAbPaymentConfirmationModal = createSelector([(store) => store.utils.featureFlags, selectIsLoggedIn], (featureFlags, isLoggedIn) => isLoggedIn &&
    checkFeatureFlag(featureFlags, 'ab_payment_confirmation_modal'));
export const selectStripePlaidAch = createSelector([
    (store) => store.utils.featureFlags,
    (store) => store.user.user.guessedHomeCountry,
], (featureFlags, guessedHomeCountry) => guessedHomeCountry === 'US' &&
    checkFeatureFlag(featureFlags, 'stripe_plaid_ach'));
export const selectRemoveScreens = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_remove_screens'));
export const selectRemoveScreensV2 = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_remove_screens_v2'));
export const selectAbDiscountAfterPaywall = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_discount_after_paywall'));
export const selectAbMaximiseTrial = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_maximise_trial'));
export const selectAbExtendTrial = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_extend_trial'));
export const selectAbFairPrice = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_fair_price'));
export const selectAbPartialRefundNewCopy = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_partial_refund_new_copy'));
export const selectAbSkipNextPayment = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_skip_next_payment'));
export const selectAnnualDiscountPercentage = createSelector([selectSubscriptionPrices, (_, tier) => tier], (prices, tier) => {
    if (!isPremiumTier(tier))
        return undefined;
    return getAnnualDiscountPercentage(prices?.[TierPeriod.monthly][tier]?.price, prices?.[TierPeriod.yearly][tier]?.price);
});
/**
 * The tier a paywall promotes when the caller doesn’t require a specific one.
 * `ab_ultimate_paywall` promotes Ultimate instead of Pro.
 */
export const selectDefaultPaywallTier = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_ultimate_paywall')
    ? Tier.ultimate
    : Tier.pro);
